/**
 * Returns a copy of the the supplied object including only properties
 * with values that are not `undefined`.
 *
 * @param obj The object to remove undefined properties from
 */
export function removeUndefinedProperties<T extends { [key in keyof T]: unknown }>(
    obj: T
): Partial<T> {
    return Object.entries(obj).reduce<Partial<T>>((result, [propName, value]) => {
        if (value === undefined) {
            return result;
        }

        result[propName as keyof T] = value as T[keyof T];
        return result;
    }, {});
}

/**
 * Compare two objects to see if they contain the same keys & values.
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @returns `true` if the two objects are the same, `false` otherwise.
 * @throws Errors if either of the provided arguments are not objects.
 */
export function objectsAreSame<T>(obj1: NonNullable<T>, obj2: NonNullable<T>) {
    if (typeof obj1 !== 'object') {
        throw new Error('obj1 is not an object');
    }
    if (typeof obj2 !== 'object') {
        throw new Error('obj2 is not an object');
    }
    if (obj1 === obj2) {
        return true;
    }
    const keys = [...Object.keys(obj1), ...Object.keys(obj2)] as (keyof T)[];
    return keys.every((key) => obj1[key] === obj2[key]);
}

/**
 * Compare two objects to see if they contain the same keys & values.
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @param comparer The function to use to compare the objects' props.
 * @returns `true` if the two objects are the same, `false` otherwise.
 * @throws Errors if either of the provided arguments are not objects.
 */
export function mapsAreSame<T, V>(
    obj1: NonNullable<{ [key in keyof T]: V }>,
    obj2: NonNullable<{ [key in keyof T]: V }>,
    propComparer: (a: V, b: V) => boolean
) {
    if (typeof obj1 !== 'object') {
        throw new Error('obj1 is not an object');
    }
    if (typeof obj2 !== 'object') {
        throw new Error('obj2 is not an object');
    }
    if (obj1 === obj2) {
        return true;
    }
    const keys = [...Object.keys(obj1), ...Object.keys(obj2)] as (keyof T)[];
    return keys.every((key) => propComparer(obj1[key], obj2[key]));
}

/**
 * Returns a new object containing only the specified properties.
 *
 * @param obj the starting objects
 * @param properties the properties to copy (shallow) from the object.
 */
export function getPartialObject<
    TObject extends Record<string, unknown>,
    TKeys extends (keyof TObject)[]
>(
    obj: TObject,
    properties: TKeys
): {
    [TProperty in TKeys[0]]: TObject[TProperty];
} {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return properties.reduce<any>((result, key) => {
        if (key in obj) {
            result[key] = obj[key];
        }
        return result;
    }, {});
}

/**
 * Returns a list of object property names.
 */
export function getPropertyNames<TObject extends Record<string, unknown>>(
    obj: TObject
): (keyof TObject)[] {
    return Object.keys(obj) as (keyof TObject)[];
}

export const hasDefinedValues = (obj: Record<string, unknown>) => {
    const vals = Object.values(obj);
    if (vals.length === 0) {
        return false;
    }
    return vals.some((val) => val);
};

type Values<T> = T[keyof T];
type Entries<T> = [keyof T, Values<T>][];

/**
 * `Object.entries()` but preserving key type
 *
 * it should be noted that `objectEntries` is an implicit assertion that _only_
 * keys defined on `T` exist, which is technically a variety of unsafe (bc TS
 * only verifies that `obj` satisfies `T` (whatever `T` was in the calling
 * context). It may contain more than that, which would make the return value of
 * `objectEntries` incorrectly typed)
 */
export function objectEntries<T extends object>(obj: T): Entries<T> {
    return Object.entries(obj) as Entries<T>;
}
